import { toast } from 'sonner';

const CHAT_SCRIPT_SRC = 'https://docs.bridged.media/-/chat.js';
const CHAT_PANEL_STYLE_ID = 'bridged-chat-panel-overrides';
const CHAT_PANEL_DESKTOP_WIDTH = '560px';

export function getDocshoundChatElement() {
  return document.querySelector('docshound-chat');
}

export function getDocshoundChatTrigger() {
  const chatEl = getDocshoundChatElement();
  if (!chatEl?.shadowRoot) return null;

  return (
    Array.from(chatEl.shadowRoot.querySelectorAll('button[type="button"]')).find(button =>
      button.querySelector('svg[width="56"]')
    ) || chatEl.shadowRoot.querySelector('button')
  );
}

export function hideDefaultChatTrigger() {
  const trigger = getDocshoundChatTrigger();
  if (!trigger) return false;

  trigger.style.visibility = 'hidden';
  trigger.style.opacity = '0';
  trigger.style.pointerEvents = 'none';
  trigger.setAttribute('aria-hidden', 'true');
  trigger.tabIndex = -1;

  // The fixed wrapper (dh-top-1/2) still intercepts clicks even when the
  // button is hidden — disable it so content underneath stays clickable.
  const wrapper = trigger.closest('.dh-fixed') || trigger.parentElement;
  if (wrapper) {
    wrapper.style.pointerEvents = 'none';
    wrapper.setAttribute('aria-hidden', 'true');
  }

  return true;
}

export function applyChatPanelStyles(shadowRoot) {
  if (!shadowRoot) return;

  let style = shadowRoot.getElementById(CHAT_PANEL_STYLE_ID);

  if (!style) {
    style = document.createElement('style');
    style.id = CHAT_PANEL_STYLE_ID;
    shadowRoot.appendChild(style);
  }

  style.textContent = `
    /* Default trigger sits middle-right and blocks clicks under it.
       Our custom Help button opens chat instead — kill the hit target. */
    .docshound-chat .dh-group.dh-fixed.dh-flex.dh-items-center.dh-justify-center {
      pointer-events: none !important;
    }

    .docshound-chat .dh-shadow-messagewindow,
    .docshound-chat .dh-shadow-lg.dh-max-w-\\[90\\%\\] {
      border-radius: 0 !important;
      pointer-events: auto !important;
    }

    .docshound-chat .lg\\:dh-rounded-l-16 {
      border-radius: 0 !important;
    }

    @media (min-width: 992px) {
      .docshound-chat .dh-shadow-messagewindow {
        width: ${CHAT_PANEL_DESKTOP_WIDTH} !important;
        min-width: ${CHAT_PANEL_DESKTOP_WIDTH} !important;
        max-width: ${CHAT_PANEL_DESKTOP_WIDTH} !important;
      }
    }
  `;
}

export function applyChatBranding() {
  const chatEl = getDocshoundChatElement();

  if (!chatEl?.shadowRoot) return false;

  applyChatPanelStyles(chatEl.shadowRoot);

  const root = chatEl.shadowRoot.querySelector('.docshound-chat');
  if (!root) return false;

  const primary = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-Color')
    .trim();

  if (primary) {
    root.style.setProperty('--color-light-trigger-background', `${primary}33`);
    root.style.setProperty('--color-light-trigger-button', primary);
    root.style.setProperty('--color-light-trigger-border', `${primary}55`);
    root.style.setProperty('--color-light-accent', primary);
    root.style.setProperty('--color-light-link', primary);
  }

  hideDefaultChatTrigger();

  return true;
}

export function waitForChatAndApply(retries = 10) {
  const success = applyChatBranding();

  if (!success && retries > 0) {
    setTimeout(() => waitForChatAndApply(retries - 1), 300);
  }
}

export function injectDocshoundChatScript() {
  if (document.querySelector(`script[src="${CHAT_SCRIPT_SRC}"]`)) {
    waitForChatAndApply();
    return;
  }

  const script = document.createElement('script');
  script.src = CHAT_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    waitForChatAndApply();
  };

  document.head.appendChild(script);
}

export function openHelpChat() {
  const trigger = getDocshoundChatTrigger();

  if (trigger) {
    trigger.click();
    hideDefaultChatTrigger();

    setTimeout(() => {
      const chatEl = getDocshoundChatElement();
      if (chatEl?.shadowRoot) {
        applyChatPanelStyles(chatEl.shadowRoot);
      }
    }, 100);

    return true;
  }

  toast.info('Help chat unavailable', {
    description: 'Please try again in a moment or contact support.',
  });

  return false;
}
